// src/pages/Dashboard/Admin/Inventaire/utils/inventaireDashboardUtils.js

import { toAbsoluteInventairePhotoUrl } from "../../../../../services/inventairePhotoServices";

export const DEFAULT_INVENTAIRE_PHOTO = "/img/no-image.jpg";

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function toStockNumber(value) {
  const number = Number.parseInt(Number(value), 10);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function getInventairePhotoSrc(photoUrl) {
  if (!photoUrl) return DEFAULT_INVENTAIRE_PHOTO;

  const absolute = toAbsoluteInventairePhotoUrl(photoUrl);

  return absolute || DEFAULT_INVENTAIRE_PHOTO;
}

export function withCacheBust(url, version) {
  if (!url) return url;
  if (!version) return url;

  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
}

export function getStockStatus(item) {
  const stock = toStockNumber(item?.stock);

  if (stock <= 0) return "out";
  if (stock <= 3) return "low";

  return "ok";
}

export function getStockStatusLabel(status) {
  const labels = {
    out: "Rupture",
    low: "Stock faible",
    ok: "En stock",
  };

  return labels[status] || "Non renseigné";
}

export function getStockStatusClass(status) {
  const classes = {
    out:
      "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
    low:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    ok:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
  };

  return (
    classes[status] ||
    "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
  );
}

export function sortInventaire(rows = [], sort = "name_asc") {
  const list = [...rows];

  return list.sort((a, b) => {
    if (sort === "name_desc") {
      return String(b?.nom || "").localeCompare(String(a?.nom || ""), "fr");
    }

    if (sort === "stock_asc") {
      return toStockNumber(a?.stock) - toStockNumber(b?.stock);
    }

    if (sort === "stock_desc") {
      return toStockNumber(b?.stock) - toStockNumber(a?.stock);
    }

    if (sort === "category_asc") {
      const categoryCompare = String(a?.categorie || "").localeCompare(
        String(b?.categorie || ""),
        "fr"
      );

      if (categoryCompare !== 0) return categoryCompare;

      return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
    }

    return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
  });
}

export function getInventaireCategories(rows = []) {
  return Array.from(
    new Set(rows.map((item) => String(item?.categorie || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterInventaire(rows = [], filters = {}) {
  const query = normalizeText(filters.query);
  const category = String(filters.category || "");
  const stockStatus = String(filters.stockStatus || "");
  const sort = String(filters.sort || "name_asc");

  const filtered = rows.filter((item) => {
    const searchable = normalizeText(
      `${item?.nom || ""} ${item?.categorie || ""} ${item?.note || ""}`
    );

    const matchQuery = !query || searchable.includes(query);
    const matchCategory = !category || String(item?.categorie || "") === category;
    const matchStockStatus =
      !stockStatus || getStockStatus(item) === stockStatus;

    return matchQuery && matchCategory && matchStockStatus;
  });

  return sortInventaire(filtered, sort);
}

export function calculateInventaireStats(allRows = [], visibleRows = []) {
  const totalObjects = allRows.length;
  const visibleObjects = visibleRows.length;

  const totalUnits = visibleRows.reduce(
    (sum, item) => sum + toStockNumber(item?.stock),
    0
  );

  const categoriesCount = getInventaireCategories(allRows).length;

  const outOfStockCount = visibleRows.filter(
    (item) => getStockStatus(item) === "out"
  ).length;

  const lowStockCount = visibleRows.filter(
    (item) => getStockStatus(item) === "low"
  ).length;

  const bestStockItem = [...visibleRows].sort(
    (a, b) => toStockNumber(b?.stock) - toStockNumber(a?.stock)
  )[0];

  return {
    totalObjects,
    visibleObjects,
    totalUnits,
    categoriesCount,
    outOfStockCount,
    lowStockCount,
    bestStockName: bestStockItem?.nom || "—",
    bestStockValue: bestStockItem ? toStockNumber(bestStockItem.stock) : 0,
  };
}

export function buildInventairePayload(formData = {}) {
  const photoUrl = String(formData.photo_url || "").trim();

  return {
    categorie: String(formData.categorie || "").trim(),
    nom: String(formData.nom || "").trim(),
    stock: toStockNumber(formData.stock),
    note: String(formData.note || "").trim() || null,
    photo_url: photoUrl || DEFAULT_INVENTAIRE_PHOTO,
  };
}